import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import { CloseIcon } from 'icons/materialIcons';

const ConnectionModal = ({
    open,
    onClose,
    integration,
    connectionType, // 'oauth', 'api_key', or 'saml'
    onConnect,
    loading = false
}) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open && integration) {
            // Initialize form data based on integration fields or connection type
            const initialData = {};
            
            if (connectionType === 'saml') {
                // Initialize SAML fields
                initialData.entityId = '';
                initialData.ssoUrl = '';
                initialData.certificate = '';
                initialData.acsUrl = '';
            } else if (integration.fields && integration.fields.length > 0) {
                // Initialize from integration fields
                integration.fields.forEach(field => {
                    initialData[field.name] = '';
                });
            }
            
            setFormData(initialData);
            setErrors({});
        }
    }, [open, integration, connectionType]);

    const handleFieldChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
        // Clear error for this field
        if (errors[fieldName]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (integration.fields && integration.fields.length > 0) {
            integration.fields.forEach(field => {
                if (field.required && !formData[field.name]) {
                    newErrors[field.name] = `${field.label} is required`;
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConnect = () => {
        if (!validateForm()) {
            return;
        }

        const connectionData = {};
        
        if (connectionType === 'api_key') {
            // For API key connections, map form fields
            if (integration.fields && integration.fields.length > 0) {
                integration.fields.forEach(field => {
                    if (field.name === 'apiKey' || field.name === 'apiToken') {
                        // Map both apiKey and apiToken to apiKey for backend
                        connectionData.apiKey = formData[field.name];
                    } else if (field.name === 'instanceUrl') {
                        connectionData.instanceUrl = formData[field.name];
                    } else {
                        connectionData[field.name] = formData[field.name];
                    }
                });
            }
        } else if (connectionType === 'oauth' && Object.keys(formData).length > 0) {
            // Some OAuth integrations might have additional fields
            Object.keys(formData).forEach(key => {
                connectionData[key] = formData[key];
            });
        } else if (connectionType === 'saml') {
            // SAML data is handled in the SAML-specific form
            connectionData.entityId = formData.entityId;
            connectionData.ssoUrl = formData.ssoUrl;
            connectionData.certificate = formData.certificate;
            connectionData.acsUrl = formData.acsUrl;
        }

        onConnect(connectionData);
    };

    if (!integration) return null;

    // For OAuth, show redirect message
    if (connectionType === 'oauth') {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Connect {integration.displayName || integration.name}</Typography>
                    <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5 }}>
                        <CloseIcon />
                    </Button>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        {loading ? (
                            <>
                                <CircularProgress sx={{ mb: 2 }} />
                                <Typography>Redirecting to {integration.displayName || integration.name}...</Typography>
                            </>
                        ) : (
                            <Typography>
                                You will be redirected to {integration.displayName || integration.name} to authorize the connection.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // For SAML connections, show SAML-specific form
    if (connectionType === 'saml') {
        const samlFields = [
            { name: 'entityId', label: 'Entity ID', required: true, placeholder: 'https://your-domain.okta.com/app/example' },
            { name: 'ssoUrl', label: 'SSO URL', required: true, placeholder: 'https://your-domain.okta.com/app/example/sso/saml' },
            { name: 'certificate', label: 'X.509 Certificate', required: true, placeholder: 'Paste the certificate here', multiline: true, rows: 4 },
            { name: 'acsUrl', label: 'ACS URL (Optional)', required: false, placeholder: 'https://your-app.com/saml/acs' }
        ];

        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Configure {integration.displayName || integration.name}</Typography>
                    <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5 }} disabled={loading}>
                        <CloseIcon />
                    </Button>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 2 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            SAML integrations require configuration. Please provide the SAML metadata from your identity provider (Okta/Azure AD).
                        </Alert>
                        {samlFields.map((field) => (
                            <TextField
                                key={field.name}
                                fullWidth
                                label={field.label}
                                type="text"
                                value={formData[field.name] || ''}
                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                error={!!errors[field.name]}
                                helperText={errors[field.name] || (field.name === 'certificate' ? 'Paste the full X.509 certificate (including BEGIN/END markers)' : '')}
                                placeholder={field.placeholder}
                                required={field.required}
                                multiline={field.multiline}
                                rows={field.rows || 1}
                                sx={{ mb: 2 }}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            const samlData = {
                                entityId: formData.entityId,
                                ssoUrl: formData.ssoUrl,
                                certificate: formData.certificate,
                                acsUrl: formData.acsUrl
                            };
                            if (!samlData.entityId || !samlData.ssoUrl || !samlData.certificate) {
                                setErrors({
                                    entityId: !samlData.entityId ? 'Entity ID is required' : '',
                                    ssoUrl: !samlData.ssoUrl ? 'SSO URL is required' : '',
                                    certificate: !samlData.certificate ? 'Certificate is required' : ''
                                });
                                return;
                            }
                            onConnect(samlData);
                        }}
                        variant="contained"
                        disabled={loading}
                        sx={{ backgroundColor: '#000', color: '#fff' }}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Configure'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // For API key connections, show form
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Connect {integration.displayName || integration.name}</Typography>
                <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5 }} disabled={loading}>
                    <CloseIcon />
                </Button>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    {integration.fields && integration.fields.length > 0 ? (
                        integration.fields.map((field) => (
                            <TextField
                                key={field.name}
                                fullWidth
                                label={field.label}
                                type={field.type === 'password' ? 'password' : 'text'}
                                value={formData[field.name] || ''}
                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                error={!!errors[field.name]}
                                helperText={errors[field.name]}
                                placeholder={field.placeholder}
                                required={field.required}
                                sx={{ mb: 2 }}
                            />
                        ))
                    ) : (
                        <Alert severity="info">
                            No configuration required for this integration.
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConnect}
                    variant="contained"
                    disabled={loading}
                    sx={{ backgroundColor: '#000', color: '#fff' }}
                >
                    {loading ? <CircularProgress size={20} /> : 'Connect'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConnectionModal;

